<template>
  <section id="milestone-section" class="milestone-section flex flex-col items-center py-20 px-6 w-full bg-blue-300">
    <div class="responsive-body-large font-semibold text-center text-black mb-4">
      {{ $t('aboutMilestone.embarking.part1') }} <br> {{ $t('aboutMilestone.embarking.part2') }}
    </div>
    <div
        class="mt-11 responsive-title-1 text-center text-black w-[839px] max-md:mt-10 max-md:max-w-full">
      {{ $t('aboutMilestone.loyalty') }}
    </div>
    <!-- Image Section that changes based on current milestone -->
    <div v-touch:swipe="handleSwipe" class="relative flex justify-center gap-5 mt-12 mb-2 sm:mb-12 overflow-hidden w-full h-[400px]">
      <transition name="fade" mode="out-in">
        <!-- Wrapper div with key for triggering transition -->
        <div :key="currentIndex" class="object-contain absolute inset-0 flex items-center justify-center w-full h-full">
          <div
              v-for="(image, index) in milestones[currentIndex].images"
              :key="index"
              :class="['polaroid-image ']"
          >
            <img :src="image.src" alt="Milestone Image" class="w-96 h-96 rounded-lg object-contain" />
          </div>
        </div>
      </transition>
    </div>

    <!-- Milestone Date -->
    <div class="responsive-title-3 font-semibold text-center text-black mt-8">
      {{ milestones[currentIndex].date }}
    </div>

    <!-- Timeline and Information Section -->
    <div class="relative flex items-center justify-center h-8 w-full max-w-[1200px] overflow-x-hidden mt-8">
      <!-- Moving Timeline Bar with Dots -->
      <div
          class="timeline-container flex items-center transition-transform duration-500 "
         
      >
        <div
            v-for="(milestone, index) in milestones"
            :key="index"
            class="flex items-center"
            :class="['timeline-segment', ' w-56 sm:w-74']"
        >
          <!-- Dot for each milestone -->
          <div class="dot w-2 h-2 rounded-full" :class="index <= currentIndex ? 'bg-gray-400' : 'bg-gray-400'"></div>
          <div
              class="timeline-line w-full h-px ml-1 mr-1 bg-gray-400"
          ></div>
        </div>
        <!-- Infinite Line Segment to the Right -->
        <div class="flex-grow h-0.5 bg-gray-400"></div>
      </div>

      <!-- Fixed Center Dot with Margins -->
      <div class="absolute z-10 w-8 h-8 bg-black rounded-full flex items-center justify-center mx-3">
        <div class="w-3 h-3 bg-blue-300 rounded-full"></div>
      </div>
    </div>

    <!-- Milestone Description -->
    <div class="mt-9  responsive-body-large text-center w-[428px] max-md:max-w-full">
      {{ milestones[currentIndex].description }}
    </div>

    <!-- Navigation Buttons -->
    <div class="flex gap-5 mt-10">
      <button
          @click="prevMilestone"
          :disabled="currentIndex === 0"
          class="p-3 rounded-full border !border-black disabled:opacity-50"
      >
        <img src="https://cdn.builder.io/api/v1/image/assets/TEMP/02dec2e545db09402ccb0d8312adfa876efae3629ed4db06b2fa47dcc7be040e?apiKey=076028e7c6ce40f6853686e5fbd07612&"
             class="w-4 aspect-square" />
      </button>
      <button
          @click="nextMilestone"
          :disabled="currentIndex === milestones.length - 1"
          class="p-3 rounded-full border !border-black disabled:opacity-50"
      >
        <img src="https://cdn.builder.io/api/v1/image/assets/TEMP/2cd1b83b6bb5a2911452e135c8e6e176a582a765de4c3fb4ed58200e06d5ef82?apiKey=076028e7c6ce40f6853686e5fbd07612&"
             class="w-4 aspect-square" />
      </button>
    </div>
    
  </section>
</template>

<script>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: "MilestoneSection",
  setup() {
    const { t } = useI18n();
    const currentIndex = ref(0);
    const isSwiping = ref(false);
    const milestones = ref([
      {
        date: t('aboutMilestone.items[0].date'),
        description: t('aboutMilestone.items[0].description'),
        images: [{ src: "/assets/about/milestone/1987.png" }]
      },
      {
        date: t('aboutMilestone.items[1].date'),
        description: t('aboutMilestone.items[1].description'),
        images: [{ src: "/assets/about/milestone/1988.jpg" }]
      },
      {
        date: t('aboutMilestone.items[2].date'),
        description: t('aboutMilestone.items[2].description'),
        images: [{ src: "/assets/about/milestone/1991.png" }]
      },
      {
        date: t('aboutMilestone.items[3].date'),
        description: t('aboutMilestone.items[3].description'),
        images: [{ src: "/assets/about/milestone/1991_london.png" }]
      },
      {
        date: t('aboutMilestone.items[4].date'),
        description: t('aboutMilestone.items[4].description'),
        images: [{ src: "/assets/about/milestone/1992.png" }]
      },
      {
        date: t('aboutMilestone.items[5].date'),
        description: t('aboutMilestone.items[5].description'),
        images: [
          { src: "/assets/about/milestone/1994.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[6].date'),
        description: t('aboutMilestone.items[6].description'),
        images: [
          { src: "/assets/about/milestone/1995.png" },
        ]
      },
      {
        date: t('aboutMilestone.items[7].date'),
        description: t('aboutMilestone.items[7].description'),
        images: [
          { src: "/assets/about/milestone/1995_fountaine_pajot.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[8].date'),
        description: t('aboutMilestone.items[8].description'),
        images: [
          { src: "/assets/about/milestone/1995_nomicos.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[9].date'),
        description: t('aboutMilestone.items[9].description'),
        images: [
          { src: "/assets/about/milestone/1996.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[10].date'),
        description: t('aboutMilestone.items[10].description'),
        images: [
          { src: "/assets/about/milestone/1997.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[11].date'),
        description: t('aboutMilestone.items[11].description'),
        images: [
          { src: "/assets/about/milestone/1998.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[12].date'),
        description: t('aboutMilestone.items[12].description'),
        images: [
          { src: "/assets/about/milestone/1999.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[13].date'),
        description: t('aboutMilestone.items[13].description'),
        images: [
          { src: "/assets/about/milestone/2000.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[14].date'),
        description: t('aboutMilestone.items[14].description'),
        images: [
          { src: "/assets/about/milestone/2001.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[15].date'),
        description: t('aboutMilestone.items[15].description'),
        images: [
          { src: "/assets/about/milestone/2002.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[16].date'),
        description: t('aboutMilestone.items[16].description'),
        images: [
          { src: "/assets/about/milestone/2006.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[17].date'),
        description: t('aboutMilestone.items[17].description'),
        images: [
          { src: "/assets/about/milestone/2008.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[18].date'),
        description: t('aboutMilestone.items[18].description'),
        images: [
          { src: "/assets/about/milestone/2009.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[19].date'),
        description: t('aboutMilestone.items[19].description'),
        images: [
          { src: "/assets/about/milestone/2010.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[20].date'),
        description: t('aboutMilestone.items[20].description'),
        images: [
          { src: "/assets/about/milestone/2011.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[21].date'),
        description: t('aboutMilestone.items[21].description'),
        images: [
          { src: "/assets/about/milestone/2012_2013.png" }
        ]
      },
      {
        date: t('aboutMilestone.items[22].date'),
        description: t('aboutMilestone.items[22].description'),
        images: [
          { src: "/assets/about/milestone/2012.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[23].date'),
        description: t('aboutMilestone.items[23].description'),
        images: [
          { src: "/assets/about/milestone/2013.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[24].date'),
        description: t('aboutMilestone.items[24].description'),
        images: [
          { src: "/assets/about/milestone/2014.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[25].date'),
        description: t('aboutMilestone.items[25].description'),
        images: [
          { src: "/assets/about/milestone/2015.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[26].date'),
        description: t('aboutMilestone.items[26].description'),
        images: [
          { src: "/assets/about/milestone/2016.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[27].date'),
        description: t('aboutMilestone.items[27].description'),
        images: [
          { src: "/assets/about/milestone/2017.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[28].date'),
        description: t('aboutMilestone.items[28].description'),
        images: [
          { src: "/assets/about/milestone/2018.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[29].date'),
        description: t('aboutMilestone.items[29].description'),
        images: [
          { src: "/assets/about/milestone/2019.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[30].date'),
        description: t('aboutMilestone.items[30].description'),
        images: [
          { src: "/assets/about/milestone/2020.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[31].date'),
        description: t('aboutMilestone.items[31].description'),
        images: [
          { src: "/assets/about/milestone/2021.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[32].date'),
        description: t('aboutMilestone.items[32].description'),
        images: [
          { src: "/assets/about/milestone/2022.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[33].date'),
        description: t('aboutMilestone.items[33].description'),
        images: [
          { src: "/assets/about/milestone/2023.jpg" }
        ]
      },
      {
        date: t('aboutMilestone.items[34].date'),
        description: t('aboutMilestone.items[34].description'),
        images: [
          { src: "/assets/about/milestone/2024.png" }
        ]
      }
      // Add the rest of the milestones here
    ]);

    const nextMilestone = () => {
      if (currentIndex.value < milestones.value.length - 1) {
        currentIndex.value += 1;
      }
    };

    const prevMilestone = () => {
      if (currentIndex.value > 0) {
        currentIndex.value -= 1;
      }
    };

    const handleSwipe = (direction) => {
      if (direction === 'left') {
        nextMilestone();
      } else if (direction === 'right') {
        prevMilestone();
      }
    };

    return {
      currentIndex,
      isSwiping,
      milestones,
      nextMilestone,
      prevMilestone,
      handleSwipe
    };
  },
  data() {
    return {
      // Legacy options if needed
    };
  },
  methods: {
    // Legacy methods if needed
  }
};
</script>


<style scoped>
.fade-enter-active,
.fade-leave-active {
  @apply transition-opacity duration-500 ease-in-out;
}
.fade-enter {
  @apply opacity-0;
}
.fade-leave-to {
  @apply opacity-0;
}
</style>
